import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormFeedback,
  CFormLabel,
  CFormText,
  CPasswordInput,
  CRow,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle } from "@coreui/icons";
import { useFailure } from "../../context/useFailure";
import { useCustomerProfile } from "../../api/hooks/useCustomerProfile";
import {
  PasswordChangeRejectedError,
  useChangePassword,
} from "../../api/hooks/useChangePassword";
import { loginRequest } from "../../config/auth";
import { routePaths } from "../../routes/route-paths";

// CStepper's exposed imperative API, as the other profile wizards declare it.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function ChangePasswordCoreui() {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");
  const { instance, accounts } = useMsal();
  const profile = useCustomerProfile();
  const changePassword = useChangePassword();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [, setCurrentStep] = useState(1);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validated, setValidated] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const [wrongCurrentPassword, setWrongCurrentPassword] = useState(false);

  const email = profile.data?.email ?? accounts[0]?.username ?? "";

  // Reusing the current password is unambiguous the moment it's typed, so it's flagged live. The rest
  // wait for a submit attempt rather than scolding someone mid-type.
  const sameAsCurrent = newPassword.length > 0 && newPassword === currentPassword;
  const currentMissing = validated && currentPassword.length === 0;
  const currentInvalid = currentMissing || wrongCurrentPassword;
  const newInvalid = sameAsCurrent || (validated && newPassword.length === 0);
  const confirmInvalid = validated && confirmPassword !== newPassword;

  const clearRejection = () => {
    if (rejection) {
      setRejection(null);
    }
  };

  const handleSubmit = async () => {
    setValidated(true);
    if (
      currentPassword.length === 0 ||
      newPassword.length === 0 ||
      sameAsCurrent ||
      confirmPassword !== newPassword
    ) {
      return;
    }

    setRejection(null);
    setWrongCurrentPassword(false);
    clearFailure();
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      stepperRef.current?.next();
    } catch (err) {
      if (err instanceof PasswordChangeRejectedError) {
        if (err.reason === "wrongCurrentPassword") {
          setWrongCurrentPassword(true);
        } else {
          setRejection(t("password.errors.rejected"));
        }
      } else {
        console.error("Password change failed", err);
        showFailure(t("password.errors.failed"));
      }
    }
  };

  // Microsoft's sign-in page, with the password prompt forced, is where its own reset link lives.
  const handleForgotPassword = () => {
    void instance.loginRedirect({
      ...loginRequest,
      prompt: "login",
      loginHint: email || undefined,
    });
  };

  const steps = [
    {
      label: t("password.steps.newPassword"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("password.form.heading")}</h4>
          </CCardHeader>
          <CCardBody>
            {rejection && (
              <CAlert color="danger" className="py-2">
                {rejection}
              </CAlert>
            )}
            <p className="text-body-secondary small">
              <Trans
                i18nKey="password.form.signedInAs"
                ns="profile"
                values={{ email }}
                components={{ 1: <strong /> }}
              />
            </p>
            <CForm
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
              }}
            >
              {/* Lets a password manager tell which saved account the new password belongs to. */}
              <input
                type="email"
                name="username"
                autoComplete="username"
                value={email}
                readOnly
                hidden
              />
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel htmlFor="current-password">{t("password.form.current")}</CFormLabel>
                  <CPasswordInput
                    id="current-password"
                    ariaLabelToggler={t("password.form.showPassword")}
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setWrongCurrentPassword(false);
                      clearRejection();
                    }}
                    invalid={currentInvalid}
                  />
                  {/* Rendered explicitly: the toggle wraps the input in its own div, so Bootstrap never
                      shows an .invalid-feedback placed after it. */}
                  {currentInvalid && (
                    <CFormFeedback invalid className="d-block">
                      {wrongCurrentPassword
                        ? t("password.errors.wrongCurrent")
                        : t("password.validation.currentRequired")}
                    </CFormFeedback>
                  )}
                  <CFormText>
                    <CButton color="link" className="p-0" onClick={handleForgotPassword}>
                      {t("password.form.forgot")}
                    </CButton>
                  </CFormText>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel htmlFor="new-password">{t("password.form.new")}</CFormLabel>
                  <CPasswordInput
                    id="new-password"
                    ariaLabelToggler={t("password.form.showPassword")}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      clearRejection();
                    }}
                    invalid={newInvalid}
                  />
                  {newInvalid && (
                    <CFormFeedback invalid className="d-block">
                      {sameAsCurrent
                        ? t("password.validation.sameAsCurrent")
                        : t("password.validation.newRequired")}
                    </CFormFeedback>
                  )}
                  <CFormText>{t("password.form.rule")}</CFormText>
                </CCol>
              </CRow>
              <CRow className="mb-4">
                <CCol md={6}>
                  <CFormLabel htmlFor="confirm-password">{t("password.form.confirm")}</CFormLabel>
                  <CPasswordInput
                    id="confirm-password"
                    ariaLabelToggler={t("password.form.showPassword")}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      clearRejection();
                    }}
                    invalid={confirmInvalid}
                  />
                  {confirmInvalid && (
                    <CFormFeedback invalid className="d-block">
                      {t("password.validation.mismatch")}
                    </CFormFeedback>
                  )}
                </CCol>
              </CRow>

              <div className="d-flex justify-content-between">
                <CButton
                  color="secondary"
                  variant="outline"
                  onClick={() => navigate(routePaths.profile)}
                  disabled={changePassword.isPending}
                >
                  {t("password.form.cancel")}
                </CButton>
                <CButton type="submit" color="primary" disabled={changePassword.isPending}>
                  {changePassword.isPending && <CSpinner size="sm" className="me-2" />}
                  {t("password.form.submit")}
                </CButton>
              </div>
            </CForm>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: t("password.steps.done"),
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">{t("password.done.heading")}</h4>
          </CCardHeader>
          <CCardBody>
            <div className="text-center py-3 mb-2">
              <CIcon icon={cilCheckCircle} size="3xl" className="text-success mb-3" />
              <h3 className="mb-2">{t("password.done.title")}</h3>
              <p className="text-body-secondary mx-auto" style={{ maxWidth: 480 }}>
                {t("password.done.body", { email })}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              <Link to={routePaths.profile} className="btn btn-primary">
                {t("password.done.backToProfile")}
              </Link>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>{t("password.title")}</h2>
      <p className="text-body-secondary">{t("password.subtitle")}</p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
